import type { Country } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Box,
  CheckboxStandalone,
  Columns,
  Column,
  Text,
  IconCritical,
} from 'braid-design-system';
import React, { type FormEvent, useEffect } from 'react';

import { useAnalyticsContext } from '../../../modules/analytics/AnalyticsContext';
import { Experience } from '../../../types';
import getClientHrefLinks from '../../../utils/getClientHrefLinks';
import getDisplayBrand from '../../../utils/getDisplayBrand';
import { useAppContext } from '../../AppContext';
import TrackLink from '../../shared/TrackLink/TrackLink';

import translations from './.vocab';
import type { ConsentOptionChangeTracking, ConsentTypeProps } from './utils';

interface Props {
  consentProps: ConsentTypeProps;
  controller: any;
  onCheckboxOptionChangeTracking: (
    options: ConsentOptionChangeTracking,
  ) => void;
}

const ConsentCheckbox = ({
  controller,
  consentProps,
  onCheckboxOptionChangeTracking,
}: Props) => {
  const context = useAppContext();
  const { locale } = context;

  const { t } = useTranslations(translations);
  const { analytics } = useAnalyticsContext();
  const privacyLink = getClientHrefLinks('/privacy/', context);
  const collectionLink = getClientHrefLinks('/collection-notice/', context);
  const { id, name, type } = consentProps;

  useEffect(() => {
    if (controller.errorMessage) {
      analytics.trackLink({
        eventName: 'alert_viewed',
        alertShownType: 'notice',
        alertShownTone: 'critical',
        alertShownPosition: 'content',
        alertShownTitle: 'consent check box unselected',
        alertShownMessage: controller.errorMessage,
      });
    }
  }, [analytics, controller.errorMessage]);

  const AGE_CONSENT_PER_LOCALE: Partial<Record<Country, number>> = {
    id: 21,
    th: 20,
    sg: 13,
    my: 18,
    ph: 18,
  };

  const getAgeConsent = (siteLocale: Country): number | '' =>
    AGE_CONSENT_PER_LOCALE[siteLocale] ?? '';

  const onConsentChange = (event: FormEvent<HTMLInputElement>) => {
    controller.onChange(event);

    // this will not track age consent
    onCheckboxOptionChangeTracking({
      eventName: 'option_selection_pressed',
      optionSelectionType: 'checkbox',
      optionSelected: controller.value ? 'unchecked' : 'checked',
      optionSelectionName: name,
      [id]: !controller.value,
    });
  };

  const getConsentKey: () => Parameters<typeof t>[0] | null = () => {
    if (!['au', 'nz'].includes(locale)) {
      if (context.experience === Experience.Candidate) {
        if (type === 'privacy' && locale === 'hk') {
          return 'privacyConsent-candidate-hk';
        }

        return `${type}Consent-candidate`;
      } else if (context.experience === Experience.Hirer) {
        return `consent_checkbox__hirer_${type}_consent__label`;
      }
    }

    return null;
  };

  const consentKey = getConsentKey();
  const consentText = consentKey
    ? t(consentKey, {
        PrivacyPolicyLink: (v: any) => (
          <TrackLink
            key="privacy-link"
            href={privacyLink}
            weight="weak"
            target="_blank"
            trackingData={{
              eventName: 'privacy_statement_pressed',
              isThirdPartyLink: 'false',
              linkPosition: 'body',
            }}
          >
            {v}
          </TrackLink>
        ),
        CollectionNoticeLink: (v: any) => (
          <TrackLink
            key="collection-link"
            href={collectionLink}
            weight="weak"
            target="_blank"
            trackingData={{
              eventName: 'collection_statement_pressed',
              isThirdPartyLink: 'false',
              linkPosition: 'body',
            }}
          >
            {v}
          </TrackLink>
        ),
        parentalConsentAge: getAgeConsent(locale as Country).toString(),
        siteName: getDisplayBrand(context.brand),
      })
    : '';

  return (
    <Box>
      <Columns space="small">
        <Column width="content">
          <Text size="xsmall">
            <CheckboxStandalone
              aria-labelledby={`${type}_agreement`}
              data={{
                testid: `${type}-chkbox`,
              }}
              size="small"
              id={`${type}-chkbox`}
              tone={Boolean(controller.valid) ? 'neutral' : 'critical'}
              required
              checked={controller.value}
              onChange={(e) => {
                onConsentChange(e);
                controller.onBlur(e);
              }}
            />
          </Text>
        </Column>
        <Column>
          <Box
            component="label"
            htmlFor={`${type}-chkbox`}
            aria-label={`${type}_agreement`}
            cursor="pointer"
          >
            <Text size="xsmall" id={`${type}_agreement`}>
              {consentText}
            </Text>
          </Box>
        </Column>
      </Columns>
      {!Boolean(controller.valid) && (
        <Box paddingTop={['medium']}>
          <Text size="small" tone="critical">
            <IconCritical /> &nbsp;
            {controller.errorMessage}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default ConsentCheckbox;
